import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Upload, Link as LinkIcon, FileText, Bot, Loader2, Check, X, 
  AlertTriangle, ChevronDown, ChevronUp, Edit2, Save, Merge,
  Trash2, CheckCircle, XCircle, RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';
import Navbar from '@/components/Navbar';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

const API = `${window.location.origin}/api`;

const CATEGORIES = [
  "Restaurants",
  "Retail",
  "Beauty & Wellness",
  "Professional Services",
  "Health & Medical",
  "Entertainment",
  "Automotive",
  "Arts & Culture",
  "Real Estate & Housing",
  "Travel & Lodging",
  "Media & Creative Agencies",
  "Community & Non-Profit",
  "Home & Construction",
  "Manufacturing & Wholesale",
  "Technology",
  "Education",
  "Other"
];

function BusinessCard({ business, onAction, onEdit }) {
  const [expanded, setExpanded] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editData, setEditData] = useState(business);
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${API}/admin/ingestion/${business.ingestion_id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(editData)
      });
      
      if (response.ok) {
        toast.success('Business updated');
        setEditing(false);
        onEdit?.(editData);
      } else {
        toast.error('Failed to save changes');
      }
    } catch (error) {
      toast.error('Error saving changes');
    } finally {
      setSaving(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className={`border rounded-xl overflow-hidden ${
        business.is_duplicate 
          ? 'border-yellow-500/30 bg-yellow-500/5' 
          : 'border-zinc-800 bg-zinc-900/50'
      }`}
    >
      {/* Header */}
      <div className="p-4 flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            {editing ? (
              <Input
                value={editData.name}
                onChange={(e) => setEditData({ ...editData, name: e.target.value })}
                className="bg-zinc-800 border-zinc-700 text-white"
              />
            ) : (
              <h3 className="font-semibold text-white truncate">{business.name}</h3>
            )}
            {business.is_duplicate && (
              <span className="shrink-0 flex items-center gap-1 px-2 py-0.5 rounded-full bg-yellow-500/20 text-yellow-400 text-xs">
                <AlertTriangle className="w-3 h-3" />
                Duplicate
              </span>
            )}
          </div>
          
          <div className="flex flex-wrap items-center gap-2 text-sm text-zinc-500">
            {business.category && <span className="text-zinc-400">{business.category}</span>}
            {business.city && <span>• {business.city}, {business.state}</span>}
            <span className="text-zinc-600">• Score: {(business.confidence_score * 100).toFixed(0)}%</span>
          </div>
        </div>

        <div className="flex items-center gap-2 shrink-0">
          <button
            onClick={() => setEditing(!editing)}
            className="w-8 h-8 rounded-lg bg-zinc-800 flex items-center justify-center text-zinc-400 hover:text-white transition-colors"
          >
            {editing ? <X className="w-4 h-4" /> : <Edit2 className="w-4 h-4" />}
          </button>
          <button
            onClick={() => setExpanded(!expanded)}
            className="w-8 h-8 rounded-lg bg-zinc-800 flex items-center justify-center text-zinc-400 hover:text-white transition-colors"
          >
            {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>
        </div>
      </div>

      {/* Expanded Details */}
      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="border-t border-zinc-800"
          >
            <div className="p-4 space-y-4">
              {editing ? (
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-zinc-400 text-xs">Phone</Label>
                    <Input
                      value={editData.phone || ''}
                      onChange={(e) => setEditData({ ...editData, phone: e.target.value })}
                      className="bg-zinc-800 border-zinc-700 text-white mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-zinc-400 text-xs">Website</Label>
                    <Input
                      value={editData.website || ''}
                      onChange={(e) => setEditData({ ...editData, website: e.target.value })}
                      className="bg-zinc-800 border-zinc-700 text-white mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-zinc-400 text-xs">Category</Label>
                    <Select
                      value={editData.category || ''}
                      onValueChange={(v) => setEditData({ ...editData, category: v })}
                    >
                      <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {CATEGORIES.map((cat) => (
                          <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-zinc-400 text-xs">City</Label>
                    <Input
                      value={editData.city || ''}
                      onChange={(e) => setEditData({ ...editData, city: e.target.value })}
                      className="bg-zinc-800 border-zinc-700 text-white mt-1"
                    />
                  </div>
                  <div className="col-span-2">
                    <Label className="text-zinc-400 text-xs">Address</Label>
                    <Input
                      value={editData.address || ''}
                      onChange={(e) => setEditData({ ...editData, address: e.target.value })}
                      className="bg-zinc-800 border-zinc-700 text-white mt-1"
                    />
                  </div>
                  <div className="col-span-2">
                    <Label className="text-zinc-400 text-xs">Description</Label>
                    <Textarea
                      value={editData.description || ''}
                      onChange={(e) => setEditData({ ...editData, description: e.target.value })}
                      className="bg-zinc-800 border-zinc-700 text-white mt-1"
                      rows={2}
                    />
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-4 text-sm">
                  {business.phone && (
                    <div><span className="text-zinc-500">Phone:</span> <span className="text-white">{business.phone}</span></div>
                  )}
                  {business.website && (
                    <div><span className="text-zinc-500">Website:</span> <a href={business.website} target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:underline">{business.website}</a></div>
                  )}
                  {business.address && (
                    <div className="col-span-2"><span className="text-zinc-500">Address:</span> <span className="text-white">{business.address}</span></div>
                  )}
                  {business.description && (
                    <div className="col-span-2"><span className="text-zinc-500">Description:</span> <span className="text-zinc-300">{business.description}</span></div>
                  )}
                </div>
              )}

              {/* Duplicate Info */}
              {business.is_duplicate && business.duplicate_business && (
                <div className="p-3 rounded-lg bg-yellow-500/10 border border-yellow-500/20">
                  <p className="text-yellow-400 text-sm font-medium mb-1">Possible duplicate of:</p>
                  <p className="text-white">{business.duplicate_business.name}</p>
                  <p className="text-zinc-400 text-sm">{business.duplicate_business.address}, {business.duplicate_business.city}</p>
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="p-4 bg-zinc-900/50 border-t border-zinc-800 flex items-center gap-2">
              {editing ? (
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg bg-white text-black font-medium hover:bg-zinc-200 transition-colors disabled:opacity-50"
                >
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                  Save Changes
                </button>
              ) : (
                <>
                  <button
                    onClick={() => onAction(business.ingestion_id, 'approve')}
                    className="flex items-center gap-2 px-4 py-2 rounded-lg bg-green-500/20 text-green-400 font-medium hover:bg-green-500/30 transition-colors"
                    data-testid={`approve-${business.ingestion_id}`}
                  >
                    <CheckCircle className="w-4 h-4" />
                    Approve
                  </button>
                  
                  {business.is_duplicate && business.duplicate_of && (
                    <button
                      onClick={() => onAction(business.ingestion_id, 'merge', business.duplicate_of)}
                      className="flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-500/20 text-blue-400 font-medium hover:bg-blue-500/30 transition-colors"
                      data-testid={`merge-${business.ingestion_id}`}
                    >
                      <Merge className="w-4 h-4" />
                      Merge
                    </button>
                  )}
                  
                  <button
                    onClick={() => onAction(business.ingestion_id, 'reject')}
                    className="flex items-center gap-2 px-4 py-2 rounded-lg bg-red-500/20 text-red-400 font-medium hover:bg-red-500/30 transition-colors"
                    data-testid={`reject-${business.ingestion_id}`}
                  >
                    <XCircle className="w-4 h-4" />
                    Reject
                  </button>
                </>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

export default function AdminIngestionPage() {
  const [sourceType, setSourceType] = useState('url');
  const [content, setContent] = useState('');
  const [crawlDepth, setCrawlDepth] = useState(1);
  const [processing, setProcessing] = useState(false);
  const [pendingBusinesses, setPendingBusinesses] = useState([]);
  const [batches, setBatches] = useState([]);
  const [selectedBatch, setSelectedBatch] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchBatches();
    fetchPending();
  }, []);

  const fetchBatches = async () => {
    try {
      const response = await fetch(`${API}/admin/ingestion/batches`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setBatches(data);
      }
    } catch (error) {
      console.error('Error fetching batches:', error);
    }
  };

  const fetchPending = async (batchId = null) => {
    setLoading(true);
    try {
      const url = batchId 
        ? `${API}/admin/ingestion/pending?batch_id=${batchId}`
        : `${API}/admin/ingestion/pending`;
      
      const response = await fetch(url, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setPendingBusinesses(data);
      }
    } catch (error) {
      console.error('Error fetching pending:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleProcess = async () => {
    if (!content.trim()) {
      toast.error('Please enter a URL or text content');
      return;
    }

    setProcessing(true);
    try {
      const response = await fetch(`${API}/admin/ingestion/process`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          source_type: sourceType,
          content: content.trim(),
          crawl_depth: crawlDepth
        })
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(`Found ${data.businesses_found} businesses!`);
        setContent('');
        fetchBatches();
        fetchPending();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Processing failed');
      }
    } catch (error) {
      toast.error('Error processing content');
    } finally {
      setProcessing(false);
    }
  };

  const handleAction = async (ingestionId, action, mergeWith = null) => {
    try {
      const response = await fetch(`${API}/admin/ingestion/${ingestionId}/action`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ action, merge_with: mergeWith })
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(data.message);
        setPendingBusinesses(prev => prev.filter(b => b.ingestion_id !== ingestionId));
        fetchBatches();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Action failed');
      }
    } catch (error) {
      toast.error('Error processing action');
    }
  };

  const handleBatchAction = async (action) => {
    if (!selectedBatch) return;

    try {
      const response = await fetch(
        `${API}/admin/ingestion/batch-action?batch_id=${selectedBatch}&action=${action}`,
        {
          method: 'POST',
          credentials: 'include'
        }
      );

      if (response.ok) {
        const data = await response.json();
        toast.success(data.message);
        fetchPending(selectedBatch);
        fetchBatches();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Batch action failed');
      }
    } catch (error) {
      toast.error('Error processing batch action');
    }
  };

  return (
    <div className="min-h-screen bg-[#050505]">
      <Navbar />

      <main className="pt-24 pb-20 px-6">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-8"
          >
            <p className="font-mono text-xs uppercase tracking-[0.2em] text-zinc-500 mb-2">
              Admin Tools
            </p>
            <h1 className="text-3xl font-bold text-white tracking-tight">
              Business Ingestion Tool
            </h1>
            <p className="text-zinc-500 mt-2">
              Import businesses from URLs or text using AI extraction
            </p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Input Form */}
            <div className="lg:col-span-1">
              <div className="glass-card rounded-xl p-6 sticky top-24">
                <h2 className="font-semibold text-white mb-4 flex items-center gap-2">
                  <Bot className="w-5 h-5 text-purple-400" />
                  AI Extraction
                </h2>

                {/* Source Type Toggle */}
                <div className="flex gap-2 mb-4">
                  <button
                    onClick={() => setSourceType('url')}
                    className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                      sourceType === 'url'
                        ? 'bg-white text-black'
                        : 'bg-zinc-900 text-zinc-400 hover:text-white'
                    }`}
                    data-testid="source-type-url"
                  >
                    <LinkIcon className="w-4 h-4" />
                    URL
                  </button>
                  <button
                    onClick={() => setSourceType('text')}
                    className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                      sourceType === 'text'
                        ? 'bg-white text-black'
                        : 'bg-zinc-900 text-zinc-400 hover:text-white'
                    }`}
                    data-testid="source-type-text"
                  >
                    <FileText className="w-4 h-4" />
                    Text
                  </button>
                </div>

                {/* Input */}
                {sourceType === 'url' ? (
                  <div className="space-y-4">
                    <div>
                      <Label className="text-zinc-400 text-sm">Website URL</Label>
                      <Input
                        value={content}
                        onChange={(e) => setContent(e.target.value)}
                        placeholder="https://directory-site.com/businesses"
                        className="bg-zinc-900 border-zinc-800 text-white mt-1"
                        data-testid="input-url"
                      />
                    </div>
                    <div>
                      <Label className="text-zinc-400 text-sm">Crawl Depth (pages)</Label>
                      <Select value={crawlDepth.toString()} onValueChange={(v) => setCrawlDepth(parseInt(v))}>
                        <SelectTrigger className="bg-zinc-900 border-zinc-800 text-white mt-1">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="1">1 page (current only)</SelectItem>
                          <SelectItem value="2">2 pages deep</SelectItem>
                          <SelectItem value="3">3 pages deep</SelectItem>
                          <SelectItem value="5">5 pages deep</SelectItem>
                        </SelectContent>
                      </Select>
                      <p className="text-zinc-600 text-xs mt-1">Higher depth = more pages crawled</p>
                    </div>
                  </div>
                ) : (
                  <div>
                    <Label className="text-zinc-400 text-sm">Paste Business Listings</Label>
                    <Textarea
                      value={content}
                      onChange={(e) => setContent(e.target.value)}
                      placeholder="Paste a list of businesses here...

Example:
Soul Food Kitchen
123 Main St, Atlanta, GA
(555) 123-4567
www.soulfoodkitchen.com

Beauty by Design
456 Oak Ave, Houston, TX
(555) 987-6543"
                      className="bg-zinc-900 border-zinc-800 text-white mt-1 h-64"
                      data-testid="input-text"
                    />
                  </div>
                )}

                {/* Process Button */}
                <button
                  onClick={handleProcess}
                  disabled={processing || !content.trim()}
                  className="w-full mt-4 btn-primary flex items-center justify-center gap-2 disabled:opacity-50"
                  data-testid="process-button"
                >
                  {processing ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Processing with AI...
                    </>
                  ) : (
                    <>
                      <Upload className="w-4 h-4" />
                      Extract Businesses
                    </>
                  )}
                </button>

                {/* Recent Batches */}
                {batches.length > 0 && (
                  <div className="mt-6 pt-6 border-t border-zinc-800">
                    <h3 className="text-sm font-medium text-zinc-400 mb-3">Recent Batches</h3>
                    <div className="space-y-2">
                      {batches.slice(0, 5).map((batch) => (
                        <button
                          key={batch.batch_id}
                          onClick={() => {
                            setSelectedBatch(batch.batch_id);
                            fetchPending(batch.batch_id);
                          }}
                          className={`w-full text-left p-3 rounded-lg transition-colors ${
                            selectedBatch === batch.batch_id
                              ? 'bg-white/10 border border-white/20'
                              : 'bg-zinc-900/50 hover:bg-zinc-900'
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <span className="text-white text-sm truncate">{batch.batch_id}</span>
                            <span className="text-xs text-zinc-500">{batch.total_count} items</span>
                          </div>
                          <div className="flex items-center gap-2 mt-1 text-xs">
                            <span className="text-yellow-400">{batch.pending} pending</span>
                            <span className="text-green-400">{batch.approved} approved</span>
                            <span className="text-red-400">{batch.rejected} rejected</span>
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Review Queue */}
            <div className="lg:col-span-2">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-semibold text-white flex items-center gap-2">
                  Review Queue
                  {pendingBusinesses.length > 0 && (
                    <span className="px-2 py-0.5 rounded-full bg-yellow-500/20 text-yellow-400 text-sm">
                      {pendingBusinesses.length}
                    </span>
                  )}
                </h2>

                <div className="flex items-center gap-2">
                  {selectedBatch && pendingBusinesses.length > 0 && (
                    <>
                      <button
                        onClick={() => handleBatchAction('approve_all')}
                        className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-green-500/20 text-green-400 text-sm hover:bg-green-500/30 transition-colors"
                        data-testid="approve-all"
                      >
                        <Check className="w-3 h-3" />
                        Approve All
                      </button>
                      <button
                        onClick={() => handleBatchAction('reject_all')}
                        className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-red-500/20 text-red-400 text-sm hover:bg-red-500/30 transition-colors"
                        data-testid="reject-all"
                      >
                        <X className="w-3 h-3" />
                        Reject All
                      </button>
                    </>
                  )}
                  <button
                    onClick={() => {
                      setSelectedBatch(null);
                      fetchPending();
                    }}
                    className="w-8 h-8 rounded-lg bg-zinc-900 flex items-center justify-center text-zinc-400 hover:text-white transition-colors"
                    data-testid="refresh-pending"
                  >
                    <RefreshCw className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {loading ? (
                <div className="space-y-4">
                  {[...Array(3)].map((_, i) => (
                    <div key={i} className="h-24 skeleton rounded-xl" />
                  ))}
                </div>
              ) : pendingBusinesses.length > 0 ? (
                <div className="space-y-4">
                  {pendingBusinesses.map((business) => (
                    <BusinessCard
                      key={business.ingestion_id}
                      business={business}
                      onAction={handleAction}
                      onEdit={(updated) => {
                        setPendingBusinesses(prev => 
                          prev.map(b => b.ingestion_id === updated.ingestion_id ? { ...b, ...updated } : b)
                        );
                      }}
                    />
                  ))}
                </div>
              ) : (
                <div className="glass-card rounded-xl p-12 text-center">
                  <Check className="w-12 h-12 text-green-400 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-white mb-2">All caught up!</h3>
                  <p className="text-zinc-500">
                    No pending businesses to review. Use the form to import more.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
